import {
  Box,
  Divider,
  Drawer,
  GlobalStyles,
  List,
  ListItemButton,
  Typography,
} from '@mui/material'
import { createRef, useCallback, useState } from 'react'
import { IconButton } from 'utoniq-ui'

import {
  ArtistDmRoomDetailQuery,
  DmRoomMessageBoxFragment,
} from '@/clients/utoniq-core/schema'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { Head } from '@/components/layouts/Head'
import { ReplyForm, ReplyFormValues } from '@/components/organisms'
import {
  DmRoomMessageList,
  DmRoomMessageListRef,
} from '@/components/organisms/dmRoom'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter } from '@/core'
import { Template } from '@/core/architecture'
import { useTranslation } from '@/core/i18n'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  artistId: string
  dmRoom: ArtistDmRoomDetailQuery['dmRoom']
  messages: DmRoomMessageBoxFragment[]
  hasNextPage: boolean
}

export type Action = {
  onReply: (inputs: ReplyFormValues) => Promise<void>
  onFetchMore: () => Promise<void>
  onReportUser: (
    contentId: string,
    userId: string,
    accountId: string
  ) => Promise<void>
}

export type ConsoleDmRoomDetailRef = DmRoomMessageListRef

export const consoleDmRoomDetailRef = createRef<ConsoleDmRoomDetailRef>()

export const ConsoleDmRoomDetailTemplate: Template<State, Action> = ({
  state,
  onReply,
  onFetchMore,
  onReportUser,
}) => {
  const { routerNames } = useRouter()
  const { t } = useTranslation('consoleDmRoomDetail')
  const { handleLoginModal } = usePrivateAction()
  const [open, setOpen] = useState<boolean>(false)

  const toggleDrawer = useCallback(
    (open: boolean) => () => {
      setOpen(open)
    },
    []
  )

  const handleReportUser = useCallback(async () => {
    // ユーザー側の情報を通報対象とする(0に対象のユーザーが必ず入っている)
    await onReportUser(
      state.dmRoom?.id ?? '',
      state.dmRoom?.users?.[0]?.id ?? '',
      state.dmRoom?.users?.[0]?.accountId ?? ''
    )
    setOpen(false)
  }, [onReportUser, state.dmRoom])

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      {/**
       * @description disabled scroll
       */}
      <GlobalStyles styles={{ body: { overflow: 'hidden' } }} />
      <Head title={routerNames.ConsoleDmRooms} />
      <ConsoleBaseLayout
        margin
        header={{
          back: true,
          title: state.dmRoom?.title ?? '',
          right: state.dmRoom ? (
            <IconButton.More
              edge="end"
              size="medium"
              color="white"
              onClick={toggleDrawer(true)}
            />
          ) : undefined,
        }}
        customNavigation={
          <ReplyForm
            onReply={async values => onReply(values)}
            userId={state.artistId}
            visibleImage
            isConsole
            singleInputMode
          />
        }
      >
        <DmRoomMessageList
          userId={state.artistId}
          messages={state.messages}
          dmRoom={state.dmRoom}
          loading={state.loading}
          hasNextPage={state.hasNextPage}
          onFetchMore={onFetchMore}
          emptyMessage={t('empty')}
          refObject={consoleDmRoomDetailRef}
        />
      </ConsoleBaseLayout>
      <Drawer disableEnforceFocus open={open} onClose={toggleDrawer(false)}>
        <Box sx={{ p: 5 }}>
          <Typography variant="h6" color="grey.600" align="center">
            {state.dmRoom?.users?.[0]?.displayName}
          </Typography>
        </Box>
        <Divider />
        <List>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            divider
            onClick={handleLoginModal(handleReportUser)}
          >
            <Typography variant="h6">{t('report')}</Typography>
          </ListItemButton>
          <ListItemButton
            component={Box}
            sx={{ height: 52 }}
            alignItems="center"
            onClick={toggleDrawer(false)}
          >
            <Typography variant="h6" align="center" sx={{ width: '100%' }}>
              {t('cancel')}
            </Typography>
          </ListItemButton>
        </List>
      </Drawer>
    </ArtistThemeProvider>
  )
}
